import { useState } from 'react';
import axios from 'axios';
import { sendPasswordResetEmail } from 'firebase/auth';
import { Loader2, Mail, ShieldCheck, HelpCircle } from 'lucide-react';
import { auth } from '../firebase';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Reusable account-recovery dialogs:
 *   <ForgotPasswordDialog open onClose initialEmail />
 *   <ForgotUsernameHelpDialog open onClose />
 *
 * Designed to be dropped into ANY login surface (Auth page, Settings,
 * Welcome modal, etc.) — no router or context dependencies.
 *
 * Reset email flow (primary): backend → Resend.
 *   POST /api/auth/password-reset/request — always returns neutral OK so
 *   the UI can't be used to enumerate registered emails.  User receives a
 *   tokenized link → /reset-password?token=... → the user sets a new
 *   password via /api/auth/password-reset/confirm.
 *
 * Firebase `sendPasswordResetEmail` remains as a silent best-effort
 * fallback ONLY when the backend itself is unreachable (network error) —
 * accounts created before the Resend flow shipped may still have a
 * Firebase-managed password and benefit from the dual path.  The
 * fallback is invisible to the user; the neutral success copy never
 * changes.
 *
 * The username-help dialog NEVER queries the backend — it just explains
 * that ZenScroll logins use email and points to support.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Firebase error codes that imply "we processed it but the email may not
// be on file" — folded into the neutral success path to avoid enumeration.
const SAFE_SUCCESS_CODES = new Set([
  'auth/user-not-found',
  'auth/invalid-email',     // bad format — still safe to claim success
  'auth/missing-email',
  'auth/too-many-requests', // rate-limited — show success so attacker can't probe
  'auth/internal-error',
]);

function Backdrop({ onClose }) {
  return (
    <div
      onClick={onClose}
      data-testid="recovery-backdrop"
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-md"
    />
  );
}

function Shell({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[201] w-[92vw] max-w-md rounded-3xl border border-white/10 bg-gradient-to-b from-[#101F1D] to-[#0A1514] shadow-[0_30px_80px_rgba(0,0,0,0.6)] p-6"
    >
      {children}
    </div>
  );
}

export function ForgotPasswordDialog({ open, onClose, initialEmail = '' }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setErr(null);
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setErr('Enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      // Primary: backend-owned Resend flow.  Backend always returns the
      // same neutral success message regardless of whether the email is
      // on file — so we surface success no matter what.
      await axios.post(`${API}/auth/password-reset/request`, { email: trimmed });
      setSent(true);
    } catch (backendErr) {
      // Only treat true network failures as a fallback trigger — HTTP
      // 4xx/5xx with a JSON body indicate the backend is reachable, so
      // we still show the neutral success message (no enumeration leak).
      const isNetworkError = !backendErr?.response;
      if (!isNetworkError) {
        setSent(true);
      } else {
        // Fallback: best-effort Firebase reset for legacy accounts.
        try {
          await sendPasswordResetEmail(auth, trimmed);
          setSent(true);
        } catch (fbErr) {
          const code = fbErr?.code || '';
          if (code === 'auth/network-request-failed') {
            setErr('Could not reach the network. Please try again.');
          } else if (SAFE_SUCCESS_CODES.has(code) || true /* default-safe */) {
            setSent(true);
          }
        }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Backdrop onClose={onClose} />
      <Shell testid="forgot-password-dialog">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#1C4749]/30 border border-[#5EEAD4]/30 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#5EEAD4]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#5EEAD4]/70 font-semibold">Account recovery</p>
            <h2 className="text-white text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
              Reset your password
            </h2>
          </div>
        </div>

        {sent ? (
          <div data-testid="forgot-password-success">
            <p className="text-sm text-white/90 leading-relaxed">
              If an account exists for <span className="text-[#5EEAD4]">{email.trim() || 'that email'}</span>,
              password reset instructions have been sent. Check your inbox (and spam folder).
            </p>
            <button
              type="button"
              data-testid="forgot-password-close"
              onClick={onClose}
              className="mt-5 w-full py-2.5 rounded-full bg-[#1C4749] hover:bg-[#2A6F72] text-white text-sm font-semibold transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <p className="text-sm text-white/70 leading-relaxed mb-4">
              Enter the email you used to sign up. We&apos;ll send a secure password reset link.
            </p>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 w-4 h-4 text-white/40" />
              <input
                type="email"
                autoComplete="email"
                autoFocus
                data-testid="forgot-password-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full pl-10 pr-3 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-[#5EEAD4]/40"
              />
            </div>
            {err && (
              <p data-testid="forgot-password-error" className="text-xs text-rose-400 mt-2">{err}</p>
            )}
            <div className="flex items-center gap-2 mt-5">
              <button
                type="button"
                onClick={onClose}
                data-testid="forgot-password-cancel"
                className="px-5 py-2.5 rounded-full text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-password-submit"
                className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-[#1C4749] hover:bg-[#2A6F72] text-white font-semibold text-sm transition-colors disabled:opacity-60"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
            </div>
          </form>
        )}
      </Shell>
    </>
  );
}

export function ForgotUsernameHelpDialog({ open, onClose }) {
  if (!open) return null;
  return (
    <>
      <Backdrop onClose={onClose} />
      <Shell testid="forgot-username-dialog">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#1C4749]/30 border border-[#5EEAD4]/30 flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-[#5EEAD4]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#5EEAD4]/70 font-semibold">Need help finding your login?</p>
            <h2 className="text-white text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
              Forgot username or email
            </h2>
          </div>
        </div>

        <div className="text-sm text-white/85 leading-relaxed space-y-3">
          <p>
            Your ZenScroll <span className="font-semibold text-white">username</span> (e.g.
            <span className="text-[#5EEAD4]"> @yourname</span>) is different from your <span className="font-semibold text-white">login email</span>.
            To sign in, use the email address you registered with.
          </p>
          <p>
            Try checking your inbox for previous ZenScroll emails — your sign-up confirmation
            will show the email you used.
          </p>
          <p className="text-white/65">
            Still stuck? Email <a href="mailto:support@zen-scroll.com" className="text-[#5EEAD4] hover:underline">support@zen-scroll.com</a> with
            what you remember (display name, approximate sign-up date) and we&apos;ll help you
            recover your account.
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          data-testid="forgot-username-close"
          className="mt-5 w-full py-2.5 rounded-full bg-[#1C4749] hover:bg-[#2A6F72] text-white text-sm font-semibold transition-colors"
        >
          Got it
        </button>
      </Shell>
    </>
  );
}
